import { EmailBulkSendAction, type EmailBulkSendSummaryFieldsFragment } from '~/api/graphql';
import { formatSendTime, formatTimeOmittingSameDay } from '~/lib/scheduled-send';

type Batch = EmailBulkSendSummaryFieldsFragment;
type Progress = Batch['progress'];

/** How often a batch reads its progress while it isn't sending. */
const PROGRESS_POLL_MS = 15_000;
/** Emails go out about 10 seconds apart, so a sending batch reads often enough to show each one. */
const SENDING_POLL_MS = 5_000;
/** A batch scheduled for later starts reading its progress this long before it begins. */
const POLL_LEAD_MS = 60_000;
/**
 * How long after Send a batch with nothing waiting keeps reading its progress: Move all to
 * drafts saves its emails after Send returns, so its counts grow while nothing waits.
 */
const HAND_OFF_WINDOW_MS = 5 * 60_000;

const settledCount = (progress: Progress) =>
  progress.sent + progress.failed + progress.cancelled + progress.inDrafts;

/**
 * Emails still to go out (Queued for Send all, Scheduled for Schedule all) and the cancelled
 * ones. Once the rest is cancelled, only the one being sent still waits.
 */
export const batchTally = (batch: Batch) => {
  const { progress } = batch;
  const unsettled = progress.total - settledCount(progress);
  return batch.cancelledAt
    ? { waiting: progress.sending, cancelled: progress.cancelled + unsettled - progress.sending }
    : { waiting: unsettled, cancelled: progress.cancelled };
};

/** What decides how a batch reads its progress. firstSendAtMs is 0 for a batch that sends nothing. */
type BatchTiming = {
  waiting: number;
  firstSendAtMs: number;
  sentAtMs: number;
  isWaitingForMailbox: boolean;
};

/** Whether the batch's emails are going out now, rather than waiting for their time or turn. */
export const isBatchSending = (batch: BatchTiming, nowMs: number): boolean =>
  batch.waiting > 0 &&
  batch.firstSendAtMs > 0 &&
  batch.firstSendAtMs <= nowMs &&
  !batch.isWaitingForMailbox;

/**
 * When to start reading a batch's progress again, how often, and when to stop and ask again.
 * Right after Send it reads while the batch's emails are handed off, even when the first one
 * goes out much later. Null when nothing more will change.
 */
export const batchProgressPolling = (
  batch: BatchTiming,
  nowMs: number
): { startInMs: number; stopInMs: number | null; intervalMs: number } | null => {
  const handOffEndsInMs = batch.sentAtMs + HAND_OFF_WINDOW_MS - nowMs;
  const handOffPolling =
    handOffEndsInMs > 0
      ? { startInMs: 0, stopInMs: handOffEndsInMs, intervalMs: PROGRESS_POLL_MS }
      : null;
  if (batch.waiting === 0) {
    return handOffPolling;
  }
  const startInMs = Math.max(0, batch.firstSendAtMs - nowMs - POLL_LEAD_MS);
  if (startInMs > 0 && handOffPolling) {
    return handOffPolling;
  }
  return {
    startInMs,
    stopInMs: null,
    intervalMs: isBatchSending(batch, nowMs) ? SENDING_POLL_MS : PROGRESS_POLL_MS
  };
};

/**
 * "Sending 22 of 38" once the current round's first email has gone to the mailbox and others
 * still wait, counting that round's emails: after Send the rest, only the rest. Null before
 * that, so a round waiting its turn shows the batch's counts instead.
 */
export const batchSendingHeadline = (batch: Batch): string | null => {
  const round = batch.roundProgress;
  const hasStarted = round.sent + round.sending + round.failed > 0;
  const isMidSend =
    batch.action !== EmailBulkSendAction.MoveToDrafts &&
    !batch.cancelledAt &&
    hasStarted &&
    batchTally(batch).waiting > 0;
  return isMidSend ? `Sending ${round.sent} of ${round.total}` : null;
};

/** What a batch waiting its turn says, without times or rates. */
export const MAILBOX_WAIT_LINE = 'Starts after another bulk send from this mailbox finishes.';

/** A sent bulk email's Status in Outgoing, and the kind of state it names. */
export type BatchStatus = {
  label: string;
  kind: 'sending' | 'waiting' | 'scheduled' | 'sent' | 'failed' | 'cancelled' | 'inDrafts';
  title?: string;
};

/**
 * How far a sent batch has got, in counts only, for its row in Outgoing: while it sends, how
 * far its current round has got; once done, the whole batch. "38 failed" is a batch where
 * nothing went out.
 */
export const batchStatus = (batch: Batch, nowMs: number): BatchStatus => {
  const { progress } = batch;
  if (batch.action === EmailBulkSendAction.MoveToDrafts) {
    return { label: 'In drafts', kind: 'inDrafts' };
  }
  if (batch.cancelledAt) {
    return { label: `Cancelled after ${progress.sent}`, kind: 'cancelled' };
  }
  // A batch with no emails counted yet is still handing them off.
  const isDone = batchTally(batch).waiting === 0 && progress.total > 0;
  if (isDone && progress.failed > 0) {
    const label =
      progress.sent === 0
        ? `${progress.failed} failed`
        : `Sent ${progress.sent} · ${progress.failed} failed`;
    return { label, kind: 'failed' };
  }
  // Cancelling one email at a time leaves the batch itself uncancelled.
  if (isDone && progress.cancelled > 0) {
    return { label: `Sent ${progress.sent} · ${progress.cancelled} cancelled`, kind: 'cancelled' };
  }
  if (isDone) {
    return { label: 'Sent', kind: 'sent' };
  }
  if (batch.isWaitingForMailbox) {
    return { label: 'Queued', kind: 'waiting', title: MAILBOX_WAIT_LINE };
  }
  const firstSendAtMs = batch.firstSendAt ? new Date(batch.firstSendAt).getTime() : 0;
  if (firstSendAtMs > nowMs) {
    return { label: 'Scheduled', kind: 'scheduled' };
  }
  const round = batch.roundProgress;
  return { label: `Sending ${round.sent} of ${round.total}`, kind: 'sending' };
};

/** The drafted emails Send left out, which stay drafts in Cara. */
export const heldBackCount = (batch: Batch) => ({
  count: batch.progress.heldBack,
  label: 'with issues'
});

/** What the emails still to go out are called, by what Send did. */
const WAITING_COUNT_LABEL: Record<EmailBulkSendAction, string> = {
  [EmailBulkSendAction.SendAll]: 'queued',
  [EmailBulkSendAction.ScheduleAll]: 'scheduled',
  [EmailBulkSendAction.MoveToDrafts]: 'saving to drafts'
};

/**
 * How many recipients show each status in the batch's recipient list, so the counts add up to
 * the recipients: the batch's own emails, plus those held back and later sent or saved on
 * their own. In the order the batch's action makes them matter, leaving out empty ones but the
 * first.
 */
export const batchCounts = (batch: Batch): { count: number; label: string }[] => {
  const { progress, outsideBatchProgress: outside } = batch;
  const { waiting, cancelled } = batchTally(batch);
  const action = batch.action ?? EmailBulkSendAction.SendAll;
  const outsideWaiting =
    outside.total - outside.sent - outside.failed - outside.cancelled - outside.inDrafts;
  // An email scheduled on its own shows as Scheduled, which a Schedule all's own count names.
  const isOutsideWaitingCountedAsBatch = action === EmailBulkSendAction.ScheduleAll;
  const sentCount = { count: progress.sent + outside.sent, label: 'sent' };
  const waitingCount = {
    count: waiting + (isOutsideWaitingCountedAsBatch ? outsideWaiting : 0),
    label: WAITING_COUNT_LABEL[action]
  };
  const outsideScheduledCount = {
    count: isOutsideWaitingCountedAsBatch ? 0 : outsideWaiting,
    label: WAITING_COUNT_LABEL[EmailBulkSendAction.ScheduleAll]
  };
  const inDraftsCount = { count: progress.inDrafts + outside.inDrafts, label: 'in drafts' };
  const endedCounts = [
    { count: progress.failed + outside.failed, label: 'failed' },
    { count: cancelled + outside.cancelled, label: 'cancelled' }
  ];
  const counts =
    action === EmailBulkSendAction.MoveToDrafts
      ? [inDraftsCount, waitingCount, sentCount, outsideScheduledCount, ...endedCounts]
      : [sentCount, waitingCount, outsideScheduledCount, ...endedCounts, inDraftsCount];
  const notSentCount = { count: progress.notSent, label: 'not sent' };
  return [...counts, heldBackCount(batch), notSentCount].filter(
    ({ count }, index) => count > 0 || index === 0
  );
};

export const batchByline = (batch: Batch, now: Date): string => {
  const sender = batch.sentBy?.name ?? 'Someone';
  const sentAt = batch.sentAt ? formatTimeOmittingSameDay(new Date(batch.sentAt), now) : '';
  switch (batch.action) {
    case EmailBulkSendAction.MoveToDrafts:
      return `Saved by ${sender} to ${batch.fromAddress} · ${sentAt}`;
    case EmailBulkSendAction.ScheduleAll:
      return `Scheduled by ${sender} from ${batch.fromAddress} · ${sentAt}`;
    default:
      return `Sent by ${sender} from ${batch.fromAddress} · Started ${sentAt}`;
  }
};

export const batchStatusLine = (batch: Batch, opts: { now: Date; mailClient: string }): string => {
  const { progress } = batch;
  const { now, mailClient } = opts;
  if (batch.action === EmailBulkSendAction.MoveToDrafts) {
    return `Each email is its own draft in ${mailClient}. Open one to send it, or send it from ${mailClient}.`;
  }
  const { waiting, cancelled } = batchTally(batch);
  if (batch.cancelledAt) {
    const sender = batch.sentBy?.name ?? 'The sender';
    return `${sender} cancelled the rest at ${formatTimeOmittingSameDay(new Date(batch.cancelledAt), now)}. The ${cancelled} that didn't go out are drafts again.`;
  }
  // A batch with no emails counted yet is still handing them off.
  if (waiting === 0 && progress.total > 0) {
    return progress.failed === 0
      ? `All ${progress.sent} went out.`
      : `Done. ${progress.failed} didn't go out. Each one says why under its subject and can be sent again.`;
  }
  if (batch.isWaitingForMailbox) {
    return MAILBOX_WAIT_LINE;
  }
  const firstSendAt = batch.firstSendAt ? new Date(batch.firstSendAt) : undefined;
  if (batch.action === EmailBulkSendAction.ScheduleAll && firstSendAt && firstSendAt > now) {
    return `First one goes out ${formatSendTime(firstSendAt)}. You can close this page.`;
  }
  return 'You can close this page.';
};
